import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {useTranslation} from 'next-i18next';
import {useMutation, useQueryClient} from 'react-query';
import {votingApi} from '@src/api/voting-api';
import {CustomButton} from '@src/common/inputs/CustomButton';
import {VoteDetailsById} from '@src/models/voting';
import {votingKeys} from '@src/queries/voting.queires';
import {parseError} from '@src/utils/error';
import {toast} from '@src/utils/toast';
import VotingInfoBox from './VotingInfoBox';

interface VoteAnswerPickerProps {
  data: VoteDetailsById;
  /** False shows the answers read-only (e.g. already voted). */
  canVote: boolean;
  onVoted?: () => void;
}

/** Answer list with the "send vote" action - shared by the vote screen and the new-vote modal. */
export const VoteAnswerPicker = ({
  data,
  canVote,
  onVoted,
}: VoteAnswerPickerProps) => {
  const {t} = useTranslation();
  const queryClient = useQueryClient();
  const [vote, setVote] = useState(data.userAnswer?.id ?? '');

  useEffect(() => {
    if (data.userAnswer) {
      setVote(data.userAnswer.id);
    }
  }, [data.userAnswer]);

  const voteMutation = useMutation(
    () => votingApi.voteForAnswer({answerId: vote, voteId: data.id}),
    {
      async onSuccess() {
        toast.success(t('common:vote-modal:sent'));
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: votingKeys.votingListDetails(data.id),
          }),
          queryClient.invalidateQueries({queryKey: ['votingList']}),
        ]);
        onVoted?.();
      },
      onError(e) {
        toast.error(parseError(e).message);
      },
    }
  );

  return (
    <>
      <AnswersSection>
        {data.answers.map((x) => (
          <AnswerButton
            key={x.answer.id}
            type="button"
            disabled={!canVote}
            onClick={() => setVote(x.answer.id)}
          >
            <VotingInfoBox
              imageId={x.image ? x.image.id : ''}
              currentVote={vote}
              data={x}
            />
          </AnswerButton>
        ))}
      </AnswersSection>

      {canVote ? (
        <ActionsBar>
          <CustomButton
            label={t('common:vote-modal:send')}
            style={{maxWidth: 320, width: '100%'}}
            buttonType="primary"
            disabled={vote === '' || voteMutation.isLoading}
            onClick={() => voteMutation.mutate()}
          />
        </ActionsBar>
      ) : null}
    </>
  );
};

const AnswersSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const AnswerButton = styled.button`
  display: block;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: inherit;

  &:disabled {
    cursor: default;
  }
`;

const ActionsBar = styled.div`
  width: 100%;
  max-width: 620px;
  margin: 4px auto 0;
  display: flex;
  justify-content: center;
`;
